import React, { useContext } from "react";
import {
    Box,
    Container,
    Typography,
    Stack,
    Grid,
    Card,
    CardContent,
    Divider,
    Button,
} from "@mui/material";
import TerrainIcon from "@mui/icons-material/Terrain";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GroupsIcon from "@mui/icons-material/Groups";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PublicIcon from "@mui/icons-material/Public";
import WineBarIcon from "@mui/icons-material/WineBar";
import { LangContext } from "../App";
import Seo from "../components/Seo/Seo";

const content = {
    en: {
        kicker: "Ayrarat Tours",
        heroTitle: "Travel Rooted in Heritage",
        heroText:
            "Ayrarat Tours creates travel that connects heritage, place, and people — where journeys begin with understanding and unfold with quiet meaning. We design cultural, spiritual, and experiential journeys across Armenia, Europe, and beyond, guided by depth, respect, and authenticity. For us, travel is not merely movement across geography; it is a way of understanding place, memory, and human connection.",
        meaningTitle: "The Meaning of Ayrarat",
        meaningParagraphs: [
            "The name Ayrarat refers to the central and largest province of historic Greater Armenia — a region that has been, and remains, the political, cultural, and spiritual heart of Armenian civilization for thousands of years. It is here that the biblical Mount Ararat rises, where, according to the Bible, Noah's Ark came to rest and the first grapevine was planted in the Ararat Valley. Across different eras, this land hosted most of the thirteen historic capitals of Greater Armenia, including the modern capital, Yerevan.",
            "We chose this name deliberately. Ayrarat is not merely a place, but a symbol of origin — a reminder of continuity, responsibility, and heritage passed down from generation to generation.",
            "For Ayrarat Tours, Ayrarat symbolizes the beginning of every journey. It reflects our belief that travel should begin with respect — for history, for people, and for places that hold memory.",
        ],
        philosophyTitle: "Our Philosophy",
        philosophyIntro:
            "At Ayrarat Tours, we believe that meaningful travel is built on understanding rather than speed. Our journeys are designed to move beyond surface impressions, offering travelers space to listen, observe, and truly engage. We focus on:",
        philosophyItems: [
            { icon: "menuBook", title: "Cultural Depth", text: "Cultural depth over mass tourism." },
            { icon: "groups", title: "Authentic Encounters", text: "Authentic encounters with local communities." },
            { icon: "schedule", title: "Thoughtful Pace", text: "Thoughtfully paced itineraries." },
            { icon: "heart", title: "Respect", text: "Respect for heritage, faith, and tradition." },
        ],
        philosophyOutro: "Every route we design is shaped with care, context, and intention.",
        borderlessTitle: "Armenian Heritage Beyond Borders",
        borderlessParagraphs: [
            "Armenian history does not end with the borders of the modern state. For centuries, Armenian cultural, spiritual, and intellectual life has extended across continents, shaped by movement, resilience, and dialogue with the wider world.",
            "Ayrarat Tours curates journeys to the Lesser Armenias of Europe, the Middle East, Africa, and beyond — revealing the broader Armenian presence across the wider Armenian world. Our routes lead to historic centers such as Vienna and Venice, the Armenian Quarter of Old Jerusalem, as well as living Armenian heritage across Italy, France, Portugal, Ethiopia, Singapore, and other regions.",
            "These journeys reveal a living legacy — one that continues to connect faith, culture, and identity across places and generations.",
        ],
        europeTitle: "European Cultural Journeys",
        europeParagraphs: [
            "Alongside our Armenian heritage routes, Ayrarat Tours operates a dedicated European Tours department, shaped around Europe's rich and diverse cultural landscape.",
            "Our journeys across Europe invite travelers to experience places through their traditions and everyday life — from historic city walks and local cultural encounters to wine and gastronomy tastings, cooking classes, artisan workshops, and carefully curated heritage routes.",
            "Each itinerary is created in close collaboration with local experts, ensuring an authentic connection to place, people, and tradition.",
        ],
        closingText:
            "This is the essence of Ayrarat Tours — travel shaped by heritage, grounded in place, and defined by human connection.",
        cta: "Explore Our Tours",
    },
    de: {
        kicker: "Ayrarat Tours",
        heroTitle: "Reisen, verwurzelt im Erbe",
        heroText:
            "Ayrarat Tours gestaltet Reisen, die Erbe, Orte und Menschen miteinander verbinden – Reisen, die aus Verständnis entstehen und sich mit stiller Tiefe entfalten. Wir entwerfen kulturelle, spirituelle und erfahrungsorientierte Reisen durch Armenien, Europa und darüber hinaus, geleitet von Tiefe, Respekt und Authentizität. Für uns ist Reisen nicht bloß Bewegung durch geografische Räume; es ist eine Art, Orte zu verstehen, Erinnerung zu bewahren und menschliche Verbundenheit zu erleben.",
        meaningTitle: "Die Bedeutung von Ayrarat",
        meaningParagraphs: [
            "Der Name Ayrarat verweist auf die zentrale und größte Provinz des historischen Großarmeniens – eine Region, die über Jahrtausende hinweg das politische, kulturelle und spirituelle Herz der armenischen Zivilisation war und bis heute ist. Hier erhebt sich der biblische Berg Ararat, wo nach der Überlieferung die Arche Noah zur Ruhe kam und im Ararat-Tal der erste Weinstock gepflanzt wurde. In verschiedenen Epochen beherbergte dieses Land die Mehrheit der dreizehn historischen Hauptstädte Großarmeniens, darunter auch die heutige Hauptstadt Jerewan.",
            "Wir haben diesen Namen bewusst gewählt. Ayrarat ist nicht nur ein Ort, sondern ein Sinnbild des Ursprungs – ein Zeichen von Kontinuität, Verantwortung und einem Erbe, das von Generation zu Generation weitergegeben wird.",
            "Für Ayrarat Tours steht Ayrarat für den Anfang jeder Reise. Er spiegelt unsere Überzeugung wider, dass Reisen mit Respekt beginnen sollte – vor der Geschichte, vor den Menschen und vor den Orten, die Erinnerung in sich tragen.",
        ],
        philosophyTitle: "Unsere Philosophie",
        philosophyIntro:
            "Bei Ayrarat Tours glauben wir, dass sinnvolles Reisen auf Verständnis beruht – nicht auf Geschwindigkeit. Unsere Reisen sind so gestaltet, dass sie über oberflächliche Eindrücke hinausgehen und Raum geben zum Zuhören, Beobachten und echten Begegnen. Im Mittelpunkt stehen für uns:",
        philosophyItems: [
            { icon: "menuBook", title: "Kulturelle Tiefe", text: "Kulturelle Tiefe statt Massentourismus." },
            { icon: "groups", title: "Authentische Begegnungen", text: "Authentische Begegnungen mit lokalen Gemeinschaften." },
            { icon: "schedule", title: "Achtsames Tempo", text: "Bewusst und achtsam gestaltete Reiseverläufe." },
            { icon: "heart", title: "Respekt", text: "Respekt vor Erbe, Glauben und Tradition." },
        ],
        philosophyOutro: "Jede Route, die wir entwerfen, entsteht mit Sorgfalt, Kontextbewusstsein und klarer Intention.",
        borderlessTitle: "Armenisches Erbe jenseits der Grenzen",
        borderlessParagraphs: [
            "Die armenische Geschichte endet nicht an den Grenzen des modernen Staates. Seit Jahrhunderten entfaltet sich das kulturelle, geistige und intellektuelle Leben der Armenier über Kontinente hinweg – geprägt von Bewegung, Widerstandskraft und dem beständigen Dialog mit der Welt.",
            "Ayrarat Tours kuratiert Reisen zu den „kleinen Armenien“ Europas, des Nahen Ostens, Afrikas und darüber hinaus und macht so die Weite der armenischen Präsenz in der globalen armenischen Welt sichtbar. Unsere Wege führen zu historischen Zentren wie Wien und Venedig, in das Armenische Viertel der Altstadt von Jerusalem sowie zu lebendigen Zeugnissen armenischen Erbes in Italien, Frankreich, Portugal, Äthiopien, Singapur und weiteren Regionen.",
            "Diese Reisen offenbaren ein lebendiges Vermächtnis – eines, das Glauben, Kultur und Identität über Orte und Generationen hinweg miteinander verbindet.",
        ],
        europeTitle: "Europäische Kulturreisen",
        europeParagraphs: [
            "Neben unseren Routen zum armenischen Erbe betreibt Ayrarat Tours eine eigene Abteilung für Europareisen, die sich der reichen und vielfältigen Kulturlandschaft Europas widmet.",
            "Unsere Reisen durch Europa laden dazu ein, Orte durch ihre Traditionen und ihren Alltag zu erleben – bei historischen Stadtrundgängen, authentischen kulturellen Begegnungen, Wein- und Genussverkostungen, Kochkursen, Handwerksworkshops und sorgfältig kuratierten Kulturrouten.",
            "Jede Reiseroute entsteht in enger Zusammenarbeit mit lokalen Expertinnen und Experten und ermöglicht so eine echte, tiefgehende Verbindung zu Ort, Menschen und Tradition.",
        ],
        closingText:
            "Dies ist das Wesen von Ayrarat Tours – Reisen, geformt vom Erbe, verwurzelt im Ort und getragen von menschlicher Verbundenheit.",
        cta: "Unsere Touren entdecken",
    },
};

const philosophyIcons = {
    menuBook: MenuBookIcon,
    groups: GroupsIcon,
    schedule: ScheduleIcon,
    heart: VolunteerActivismIcon,
};

const NAVY = "#132937";
const GOLD = "#FF8C00";

const AboutUs = () => {
    const lang = useContext(LangContext);
    const t = content[lang] || content.en;

    return (
        <Box className="about-us-page" sx={{ bgcolor: "#fff" }}>
            <Seo title={`${t.heroTitle} — ${t.kicker}`} description={t.closingText} />
            {/* Hero */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${NAVY} 0%, #0c1c27 100%)`,
                    color: "#fff",
                    py: { xs: 10, md: 14 },
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0.08,
                        backgroundImage:
                            "radial-gradient(circle at 20% 30%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 35%)",
                    }}
                />
                <Container maxWidth="md" sx={{ position: "relative", textAlign: "center" }}>
                    <Typography
                        sx={{
                            letterSpacing: 4,
                            textTransform: "uppercase",
                            color: GOLD,
                            fontSize: 14,
                            fontWeight: 600,
                            mb: 2,
                        }}
                    >
                        {t.kicker}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: '"Work BlackI", serif',
                            fontSize: { xs: 34, md: 52 },
                            lineHeight: 1.15,
                            mb: 3,
                        }}
                    >
                        {t.heroTitle}
                    </Typography>
                    <Box sx={{ width: 70, height: 3, bgcolor: GOLD, mx: "auto", mb: 4 }} />
                    <Typography
                        sx={{
                            fontSize: { xs: 16, md: 18 },
                            lineHeight: 1.9,
                            color: "rgba(255,255,255,0.85)",
                            maxWidth: 760,
                            mx: "auto",
                        }}
                    >
                        {t.heroText}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                {/* Meaning of Ayrarat */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: { xs: 10, md: 14 } }}>
                    <Grid item xs={12} md={4}>
                        <Stack alignItems={{ xs: "center", md: "flex-start" }} spacing={2}>
                            <TerrainIcon sx={{ fontSize: 56, color: GOLD }} />
                            <Typography
                                sx={{
                                    fontFamily: '"Work BlackI", serif',
                                    fontSize: { xs: 28, md: 32 },
                                    color: NAVY,
                                    textAlign: { xs: "center", md: "left" },
                                }}
                            >
                                {t.meaningTitle}
                            </Typography>
                            <Box sx={{ width: 50, height: 3, bgcolor: GOLD }} />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={2.5}>
                            {t.meaningParagraphs.map((p, i) => (
                                <Typography
                                    key={i}
                                    sx={{ color: "#4a4a4a", fontSize: 16.5, lineHeight: 1.9 }}
                                >
                                    {p}
                                </Typography>
                            ))}
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: { xs: 10, md: 14 } }} />

                {/* Philosophy */}
                <Box sx={{ mb: { xs: 10, md: 14 } }}>
                    <Stack alignItems="center" spacing={2} sx={{ mb: 6, textAlign: "center" }}>
                        <Typography
                            sx={{
                                fontFamily: '"Work BlackI", serif',
                                fontSize: { xs: 28, md: 36 },
                                color: NAVY,
                            }}
                        >
                            {t.philosophyTitle}
                        </Typography>
                        <Box sx={{ width: 50, height: 3, bgcolor: GOLD }} />
                        <Typography
                            sx={{
                                color: "#4a4a4a",
                                fontSize: 16.5,
                                lineHeight: 1.9,
                                maxWidth: 760,
                            }}
                        >
                            {t.philosophyIntro}
                        </Typography>
                    </Stack>

                    <Grid container spacing={3}>
                        {t.philosophyItems.map((item) => {
                            const Icon = philosophyIcons[item.icon];
                            return (
                                <Grid item xs={12} sm={6} md={3} key={item.title}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            height: "100%",
                                            borderRadius: 3,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            transition: "transform .25s, box-shadow .25s",
                                            "&:hover": {
                                                transform: "translateY(-6px)",
                                                boxShadow: "0 16px 32px rgba(19,41,55,0.12)",
                                                borderColor: "transparent",
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3.5, textAlign: "center" }}>
                                            <Box
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: "50%",
                                                    bgcolor: `${NAVY}10`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mx: "auto",
                                                    mb: 2,
                                                }}
                                            >
                                                <Icon sx={{ color: NAVY, fontSize: 28 }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, mb: 1, color: NAVY }}>
                                                {item.title}
                                            </Typography>
                                            <Typography sx={{ color: "text.secondary", fontSize: 14.5, lineHeight: 1.7 }}>
                                                {item.text}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Typography
                        sx={{
                            textAlign: "center",
                            mt: 5,
                            fontStyle: "italic",
                            color: NAVY,
                            fontSize: 17,
                        }}
                    >
                        {t.philosophyOutro}
                    </Typography>
                </Box>

                {/* Heritage beyond borders + Europe */}
                <Grid container spacing={5} sx={{ mb: { xs: 8, md: 10 } }}>
                    <Grid item xs={12} md={6}>
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 4,
                                p: { xs: 3, md: 4 },
                                bgcolor: "#f9f7f3",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Stack spacing={2}>
                                <PublicIcon sx={{ fontSize: 40, color: GOLD }} />
                                <Typography sx={{ fontWeight: 700, fontSize: 22, color: NAVY }}>
                                    {t.borderlessTitle}
                                </Typography>
                                {t.borderlessParagraphs.map((p, i) => (
                                    <Typography key={i} sx={{ color: "#4a4a4a", fontSize: 15.5, lineHeight: 1.85 }}>
                                        {p}
                                    </Typography>
                                ))}
                            </Stack>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 4,
                                p: { xs: 3, md: 4 },
                                bgcolor: NAVY,
                                color: "#fff",
                            }}
                        >
                            <Stack spacing={2}>
                                <WineBarIcon sx={{ fontSize: 40, color: GOLD }} />
                                <Typography sx={{ fontWeight: 700, fontSize: 22 }}>
                                    {t.europeTitle}
                                </Typography>
                                {t.europeParagraphs.map((p, i) => (
                                    <Typography key={i} sx={{ color: "rgba(255,255,255,0.85)", fontSize: 15.5, lineHeight: 1.85 }}>
                                        {p}
                                    </Typography>
                                ))}
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>

                {/* Closing */}
                <Stack
                    spacing={3}
                    alignItems="center"
                    sx={{ textAlign: "center", maxWidth: 760, mx: "auto" }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Work BlackI", serif',
                            fontSize: { xs: 22, md: 28 },
                            color: NAVY,
                            lineHeight: 1.5,
                        }}
                    >
                        {t.closingText}
                    </Typography>
                    <Button
                        href="/"
                        variant="contained"
                        sx={{
                            mt: 1,
                            bgcolor: GOLD,
                            color: "#fff",
                            px: 4,
                            py: 1.4,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: 16,
                            "&:hover": { bgcolor: "#e07c00" },
                        }}
                    >
                        {t.cta}
                    </Button>
                </Stack>
            </Container>
        </Box>
    );
};

export default AboutUs;
